import { Box, Button, Flex, Heading, Text, VStack } from '@chakra-ui/react';
import React from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { UI } from '../../../common/shells/UI';
import { ROUTES } from '../../../config';

const CASH_FLOW_IFRAME_SRC = 'https://www.concreit.com/strategies/cash-flow-internal';

export const CashFlowOfferingPage: React.FC = () => {
  const navigate = useNavigate();

  const handleInvestClick = () => {
    navigate(ROUTES.checkoutCashflow);
  };

  return (
    <UI>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Cash Flow Strategy | Concreit</title>
      </Helmet>
      <Flex
        w="100%"
        h={{ base: 'auto', lg: 'calc(100vh - 200px)' }} // Full height minus navbar and padding
        gap={{ base: 4, md: 8 }}
        direction={{ base: 'column', lg: 'row' }}
        align="stretch">
        {/* Iframe Section - Bento Box Style */}
        <Box
          flex={{ base: '1', lg: '1.8' }} // Slightly less than 2:1 ratio for better balance
          minH={{ base: '600px', lg: '100%', md: '700px' }}
          bg="gray.50"
          borderRadius="2xl"
          overflow="hidden"
          boxShadow="sm"
          border="1px solid"
          borderColor="gray.200"
          position="relative">
          <iframe
            src={CASH_FLOW_IFRAME_SRC}
            title="Cash Flow Strategy Information"
            style={{
              border: 'none',
              height: '100%',
              width: '100%',
            }}
            sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
          />
        </Box>

        {/* CTA Section - Card Style */}
        <Flex
          flex="1"
          direction="column"
          justify="center"
          bg="blue.50"
          borderRadius="2xl"
          p={{ base: 8, lg: 12, md: 10 }}
          border="1px solid"
          borderColor="blue.100"
          position="relative"
          overflow="hidden">
          {/* Background Pattern */}
          <Box
            position="absolute"
            top="-50%"
            right="-30%"
            width="300px"
            height="300px"
            bg="blue.100"
            borderRadius="full"
            opacity="0.1"
            filter="blur(40px)"
          />

          <VStack spacing={8} position="relative" zIndex={1}>
            <VStack spacing={4} textAlign="center">
              <Box bg="blue.100" p={3} borderRadius="full" mb={2}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M12 2L2 7V17C2 17.5304 2.21071 18.0391 2.58579 18.4142C2.96086 18.7893 3.46957 19 4 19H20C20.5304 19 21.0391 18.7893 21.4142 18.4142C21.7893 18.0391 22 17.5304 22 17V7L12 2Z"
                    stroke="#3182CE"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M22 7L12 12L2 7"
                    stroke="#3182CE"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path d="M12 22V12" stroke="#3182CE" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </Box>

              <Heading as="h1" size="xl" color="gray.800">
                Ready to Start Investing?
              </Heading>

              <Text fontSize="lg" color="gray.600" maxW="280px">
                Join thousands of investors building wealth through institutional-grade real estate
              </Text>
            </VStack>

            <VStack spacing={4} w="full" maxW="300px">
              <Button
                size="lg"
                colorScheme="blue"
                width="full"
                height="56px"
                fontSize="lg"
                onClick={handleInvestClick}
                _hover={{
                  boxShadow: 'lg',
                  transform: 'translateY(-2px)',
                }}
                transition="all 0.2s">
                Invest in Cash Flow
              </Button>
            </VStack>
          </VStack>
        </Flex>
      </Flex>
    </UI>
  );
};
